import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Plus, Minus } from "lucide-react";
import { Reveal, Lines, SectionTag, EASE } from "../site/Motion";
import { useApp } from "../../context/AppContext";

const HIGHLIGHTS = ["Display touch 12.3\"", "Tetto panoramico", "Cerchi in lega 16\"", "Fari full LED", "Due batterie rimovibili", "Comandi parcheggio F-TOUCH™"];
const FULL_EQUIPMENT = [
  "Fari full LED", "Cerchi in lega da 16\"", "Tetto panoramico", "Display touch 12.3\"", "Bluetooth",
  "Due porte USB interne", "Due batterie rimovibili", "Freno di stazionamento elettronico", "Alzacristalli elettrici",
  "Chiave remota", "Specchietti ripiegabili elettricamente", "Comandi esterni di parcheggio", "Sistema di allarme", "Cinture a tre punti",
];

export function PretADrive() {
  const [open, setOpen] = useState(false);
  const { colourSlug } = useApp();

  return (
    <section id="pret-a-drive" data-testid="pretadrive-section" className="relative overflow-hidden border-t border-white/10 bg-card py-28 sm:py-40">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-10">
        <SectionTag index="10" label="Prêt-à-Drive" />
        <div className="mt-10 grid gap-16 lg:grid-cols-2">
          <div>
            <h2 className="font-display font-extrabold uppercase leading-[0.92] tracking-tight">
              <Lines lines={["Prêt-à-porter.", "Prêt-à-drive."]} stagger={0.15} className="text-4xl text-white sm:text-6xl lg:text-7xl" />
            </h2>
            <Reveal delay={0.3}>
              <p className="mt-8 font-display text-xl font-bold uppercase tracking-tight text-swapa-yellow sm:text-2xl">
                One SWAPA. Fully equipped.
              </p>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-zinc-400">
                Niente configurazioni infinite. Niente liste di optional. Niente attese di produzione.
                SWAPA esiste in un'unica configurazione curata fino all'ultimo dettaglio:
                tu scegli solo il colore. Tutto il resto è già di serie.
              </p>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                Full optional. As standard.
              </p>
            </Reveal>

            <div className="mt-10 flex flex-wrap gap-2.5">
              {HIGHLIGHTS.map((h, i) => (
                <Reveal key={h} delay={0.1 + i * 0.06} y={20}>
                  <span className="inline-block border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-300">
                    {h}
                  </span>
                </Reveal>
              ))}
            </div>

            <button
              data-testid="equipment-toggle"
              onClick={() => setOpen(!open)}
              className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-swapa-yellow transition-colors hover:text-white"
            >
              {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {open ? "Chiudi dotazione" : "Scopri tutta la dotazione"}
            </button>
            <AnimatePresence>
              {open && (
                <motion.ul
                  data-testid="equipment-full-list"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="mt-6 grid grid-cols-1 gap-x-8 gap-y-2 overflow-hidden sm:grid-cols-2"
                >
                  {FULL_EQUIPMENT.map((e) => (
                    <li key={e} className="flex items-center gap-3 border-b border-white/5 py-2 text-sm text-zinc-400">
                      <span className="h-1 w-1 rotate-45 bg-swapa-yellow" aria-hidden="true" />
                      {e}
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </div>

          <Reveal delay={0.2} y={60} className="flex flex-col justify-center border border-white/10 bg-ink p-8 sm:p-12">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Swapa — configurazione unica</p>
            <p className="mt-6 font-display text-6xl font-black tracking-tight text-white sm:text-7xl">€13.990</p>
            <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500">IVA inclusa</p>
            <div className="my-8 h-px bg-white/10" aria-hidden="true" />
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-zinc-400">Prenoti oggi con</span>
              <span className="font-display text-3xl font-bold text-swapa-yellow">€200</span>
            </div>
            <Link
              to={`/preordina${colourSlug ? `?colore=${colourSlug}` : ""}`}
              data-testid="pretadrive-preorder-cta"
              className="group mt-10 inline-flex items-center justify-center gap-3 bg-swapa-yellow px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-black transition-transform duration-300 hover:-translate-y-0.5"
            >
              Pre-ordina la tua SWAPA — €200
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <p className="mt-5 text-xs leading-relaxed text-zinc-600">
              Prenotazione online di €200, integralmente detraibile dal prezzo finale e cancellabile senza penali prima del contratto definitivo.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
